import React from "react";
import AccordionTwoColumnGrid from "@/components/molecules/AccordionTwoColumnGrid";
import { useTranslation } from "react-i18next";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { Grid } from "@mantine/core";
import TextInput from "@/components/atoms/TextInput";
import { Control, Controller, useFieldArray, Path } from "react-hook-form";
import { RegisterCompanyFormInput } from "@/components/pages/RegisterCompany/validation/registerCompanyValidationSchema";

interface BankAccountProps {
  control: Control<RegisterCompanyFormInput>;
  saveDraft?: () => Promise<void>;
}

const BankAccount: React.FC<BankAccountProps> = ({ control, saveDraft }) => {
  const { t } = useTranslation();

  const { fields } = useFieldArray({
    control,
    name: "partialCompany.bankAccount" as any,
  });

  const leftContent = (
    <Stack>
      <Text size="lg" display="technical" tt="uppercase" fw={500} ff="heading" mt={20}>
        {t("registerCompany:bankAccount.title")}
      </Text>
      <Grid>
        <Grid.Col span={1} pr={0} mr={0}>
          <SVGAsset name="circle-questionmark" />
        </Grid.Col>
        <Grid.Col span={10} pl={0} ml={0}>
          <Text size="sm" color="secondary">
            {t("registerCompany:bankAccount.reason")}
          </Text>
        </Grid.Col>
      </Grid>
    </Stack>
  );

  const rightContent = (
    <Grid>
      {fields.map((field, index) => (
        <React.Fragment key={field.id}>
          <Grid.Col span={12}>
            <Controller
              name={`partialCompany.bankAccount.${index}.name` as Path<RegisterCompanyFormInput>}
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState }) => (
                <TextInput
                  label={t("registerCompany:bankAccount.name")}
                  value={(value as string) ?? ""}
                  data-testid={"bank-account-name"}
                  onChange={onChange}
                  onBlur={() => {
                    onBlur();
                    saveDraft?.();
                  }}
                  required
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Controller
              name={`partialCompany.bankAccount.${index}.accountNo` as Path<RegisterCompanyFormInput>}
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState }) => (
                <TextInput
                  data-testid={"bank-account-number"}
                  label={t("registerCompany:bankAccount.accountNo")}
                  value={(value as string) ?? ""}
                  onChange={onChange}
                  required
                  onBlur={() => {
                    onBlur();
                    saveDraft?.();
                  }}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Controller
              name={`partialCompany.bankAccount.${index}.iban` as Path<RegisterCompanyFormInput>}
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState }) => (
                <TextInput
                  label={t("registerCompany:bankAccount.iban")}
                  data-testid={"bank-account-iban"}
                  value={(value as string) ?? ""}
                  required
                  onChange={onChange}
                  onBlur={() => {
                    onBlur();
                    saveDraft?.();
                  }}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
        </React.Fragment>
      ))}
    </Grid>
  );

  return <AccordionTwoColumnGrid leftContent={leftContent} rightContent={rightContent} />;
};

export default BankAccount;
